import { rerenderElement } from '../utilities/utilities';

/**
 * Toggle link
 * A plugin for making links toggle other content (with accessibility features).
 * @version 3.0.0
 * @exports ToggleLink
 */
export default class ToggleLink {
    /**
     * Constructor
     * @public
     */
    constructor(el) {
        this.openDropdown = null;
        this.targetEl = el;

        this._initToggleLink();
        this._initToggleSelect();
        this._initAnchorSupport();
        this._initDropdown();
    }

    /**
     * Add toggle feature to the links.
     * @private
     */
    _initToggleLink() {
        const sel = '[data-type="toggle"]';
        const rootEl = this.targetEl ?? document.body;

        for (const el of rootEl.querySelectorAll(sel)) {
            const toggledElem = document.getElementById(el.getAttribute('aria-controls'));

            if (!toggledElem) {
                continue;
            }

            if (el instanceof HTMLAnchorElement && !el.getAttribute("role")) {
                el.setAttribute('role', 'button');
            }

            el.setAttribute('aria-expanded', toggledElem.getAttribute('aria-hidden') !== 'true');

            el.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();

                const toggle = event.target.matches(sel) ? event.target : event.target.closest(sel);
                const state = toggledElem.getAttribute('aria-hidden') !== 'true';

                // Close other toggles that are opened in this group.
                const group = toggledElem.getAttribute('data-togglegroup');
                if (group) {
                    for (const grp of document.querySelectorAll(`[data-togglegroup="${group}"][aria-hidden="false"]`)) {
                        const sel = `[href="#${grp.id}"]`;
                        if (document.querySelector(sel) !== toggle) {
                            this._toggleContent(sel, false);
                        }
                    }
                    // If this is the only open content in the group and we don't allow all contents to be closed, stop here.
                    if (!state && toggledElem.getAttribute('data-togglegroup-noclose') === 'true') {
                        return false;
                    }
                }

                this._toggleContent(toggle, !state);
            });

            this._initValidation(el);
        }
    }

    /**
     * Checks if element has required input fields and adds oninvalid event.
     * @param {Object} trig - The toggling element
     * @private
     */
    _initValidation(trig) {
        const elem = document.getElementById(trig.getAttribute('aria-controls'))
        if (elem) {
            elem.addEventListener('invalid', e => {
                if (e.target.matches('input[required]') && elem.getAttribute('aria-hidden') === 'true') {
                    this._toggleContent(trig, true);
                }
            });
        }
    }

    /**
      * Toggles the container controlled by the element
      * @param {HTMLElement} toggle
      * @param {Boolean} state
      * @private
      */
    _toggleContent(toggle, state) {
        if (!toggle) {
            return;
        }

        const target = document.getElementById(toggle.getAttribute('aria-controls'));

        // State
        state ??= target.getAttribute('aria-hidden') === 'true';
        target.setAttribute('aria-hidden', !state);
        toggle.setAttribute('aria-expanded', state);

        // Byt plats på texter på länk/knapp
        if (toggle.dataset.toggletext && toggle.dataset.toggletext !== toggle.innerHTML) {
            const next = toggle.dataset.toggletext;
            toggle.dataset.toggletext = toggle.innerHTML;
            toggle.innerHTML = next;
        }

        rerenderElement('lt-ie9');

        // Hantera Accordion
        const accordion = document.getElementById(`accordion-${target.id}`);
        if (toggle.matches('.education-area-page__arrow-button[aria-label]')) {
            const prefix = document.body.lang === 'en' ? (state ? 'Close' : 'Expand') : (state ? 'Stäng' : 'Expandera');
            const label = accordion?.querySelector('.education-area-page__text-left')?.innerText ?? '';
            toggle.setAttribute('aria-label', label`${prefix} ${label}`);
        }
        accordion?.classList.toggle('accordion--open', state);

        // Byt klasser
        const activeClass = toggle.getAttribute('data-active-class') || 'is-active';
        toggle.classList.toggle(activeClass, state);
        const inactiveClass = toggle.getAttribute('data-inactive-class');
        inactiveClass && toggle.classList.toggle(inactiveClass, !state);

        // Hantera ev öppen Dropdown
        this.openDropdown = state && toggle.parentNode.classList.contains('toggle-dropdown') ? toggle : null;

        // Om synliggörs, focusera på element
        if (state) {
            const focus = target.querySelector('input[type="text"]') || target.querySelector('input[type="search"]') || toggle;

            if (focus === toggle && !target.getAttribute('tabindex')) {
                target.setAttribute('tabindex', '-1');
            }

            focus.focus();
        }
    }

    /**
     * Add toggle feature to the selects.
     * @private
     */
    _initToggleSelect() {
        document.addEventListener('change', e => {
            if (e.target.matches('[data-type="toggle-select"]')) {
                document.querySelector(`[aria-controls="${e.target.value}"]`)?.click();
            }
        });
    }

    /**
     * If we got an anchor in the url, open the matching togglelink (and it's parents if they are nested).
     * @private
     */
    _initAnchorSupport() {
        if (!window.location.hash) {
            return;
        }

        const id = window.location.hash.substring(1); // Hash inkluderar '#'
        const target = document.getElementById(id);
        const toggle = document.querySelector(`[aria-controls="${id}"]`);

        if (!(target && toggle)) {
            return;
        }

        for (let exp = target.parentNode.closest('.expandable'); exp; exp = exp.closest('.expandable')) {
            const tgl = document.querySelector(`[aria-controls="${exp.id}"]`);
            this._toggleContent(tgl, true);
        }

        this._toggleContent(toggle, true);
    }

    /**
     * Attach an click-event to the window object that hides open dropdown on click outside of them
     * @private
     */
    _initDropdown() {
        if (document.querySelector('.toggle-dropdown')) {
            document.addEventListener('click', event => {
                if (this.openDropdown?.contains(event.target) === false) {
                    thid.openDropdown.click();
                }
            });
        }
    }
}
